import { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ButtonPrimary } from 'shared/components/Button';
import { ContainerMedium, ContainerSmall } from 'shared/components/Container';
import { H6, PSmall } from 'shared/components/Typography';
import { Info } from 'shared/components/Info';
import { Input } from 'shared/components/Input';
import { Spacer } from 'shared/components/Layout';
import { Tooltip } from 'shared/components/Tooltip';
import { Wrapper } from 'shared/components/Wrapper';
import { onlyDigitValidation } from 'shared/validations/validations';
import { mischka, athensGray } from 'shared/config/Colors';

const Container = styled(Wrapper)`
  background-color: ${athensGray};
  border: 2px solid ${mischka};
  border-radius: 16px;
  padding: 10px 0 10px 16px;
  margin-left: 16px;
`;

const PercentInputWrapper = styled.div`
  max-width: 112px;
  position: relative;

  &::after {
    align-items: center;
    bottom: 0;
    color: ${({ theme }) => theme.silverSandTwo};
    content: '%';
    display: flex;
    font-size: 14px;
    left: calc(100% + 7px);
    margin: auto;
    position: absolute;
    top: 0;
  }
`;

const InputLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  text-align: center;
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface FreightProps {
  canEdit: boolean;
  totalExcludedNetPrice: number;
  defaultFreight: number; // Default Dealer Freight Percentage
  customFreight: number | null; // Custom Dealer Freight Percentage
  onFreightChange: (price: number) => void;
  onSave?: (customFreightAmount: number | null) => void;
}

export const Freight: FC<FreightProps> = ({
  canEdit,
  totalExcludedNetPrice,
  defaultFreight,
  customFreight,
  onFreightChange,
  onSave,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const freight = watch('freight');
  const [initialFreight, setInitialFreight] = useState<number | null>(null);

  const isFreightNullOrEmpty = (
    value: string | number | null | undefined
  ): boolean => {
    return value == null || value === '';
  };

  // Initialize the freight field with customFreight when component mounts or customFreight changes
  useEffect(() => {
    const initialValue = customFreight ?? null;
    setValue('freight', customFreight != null ? customFreight.toString() : '');
    setInitialFreight(initialValue);
  }, [customFreight, setValue]);

  // Check if freight has been changed from initial value
  const hasFreightChanged = () => {
    // Convert current form value to number or null
    const currentValue = isFreightNullOrEmpty(freight)
      ? null
      : parseFloat(freight);

    // Compare with initial value
    return currentValue !== initialFreight;
  };

  const getFreightPrice = useCallback(() => {
    // Always use what's in the Dealer Custom input field
    // If the field has a value (including 0), use that
    // If the field is empty, use defaultFreight
    const freightValue = !isFreightNullOrEmpty(freight)
      ? freight
      : defaultFreight;

    if (!errors.freight) {
      const numValue = parseFloat(freightValue.toString());
      if (!Number.isNaN(numValue)) {
        return (totalExcludedNetPrice * numValue) / 100;
      }
    }

    return 0;
  }, [freight, defaultFreight, totalExcludedNetPrice, errors.freight]);

  // Notify parent component when freight price changes
  useEffect(() => {
    const price = getFreightPrice();
    onFreightChange(price);
  }, [getFreightPrice, onFreightChange]);

  return (
    <Container>
      <Wrapper flex middle>
        <ContainerMedium isTableCell flex middle mrAuto>
          <H6>FREIGHT</H6>

          <Spacer w="10px" />

          <Info>
            <Tooltip position="right">
              Calculated based on NET value of excluded total.
            </Tooltip>
          </Info>
        </ContainerMedium>

        <ContainerMedium
          flex
          middle
          center
          isTableCell
          border={['right']}
          noVertialSpacing
        >
          <InputColumn>
            <InputLabel>Dealer Default</InputLabel>
            <PercentInputWrapper>
              <Input
                value={defaultFreight}
                readOnly
                disabled
                className="review-pricing-percentage-input"
              />
            </PercentInputWrapper>
          </InputColumn>

          <Spacer w="40px" />

          <InputColumn>
            <InputLabel>Dealer Custom</InputLabel>
            <PercentInputWrapper>
              {/* 0 - 99.99 */}
              <Input
                {...register(
                  'freight',
                  onlyDigitValidation({ min: 0, max: 99.99 })
                )}
                readOnly={!canEdit}
                aria-invalid={errors.freight ? 'true' : 'false'}
                className="review-pricing-percentage-input"
                placeholder=""
              />
            </PercentInputWrapper>
          </InputColumn>

          {hasFreightChanged() && (
            <>
              <Spacer w="40px" />
              <ButtonPrimary
                disabled={!canEdit || !!errors.freight}
                onClick={() => {
                  // If field is empty, send null to clear custom freight
                  // Otherwise send the parsed number value
                  const customFreightAmount = isFreightNullOrEmpty(freight)
                    ? null
                    : parseFloat(freight);

                  onSave?.(customFreightAmount);
                  // Update initial freight to current value after save
                  setInitialFreight(customFreightAmount);
                }}
              >
                Save
              </ButtonPrimary>
            </>
          )}
        </ContainerMedium>

        <ContainerSmall isTableCell>
          <PSmall>{getFreightPrice().toFixed(2)}</PSmall>
        </ContainerSmall>
      </Wrapper>
    </Container>
  );
};
